/**
 * App — root component with URL-based routing (react-router-dom).
 *
 * 5 primary tabs:
 *   /today       Morning Brief + decisions (default)
 *   /portfolio   Positions + performance + theses
 *   /research    Thesis wizard + research tools
 *   /approvals   Pending approval queue
 *   /settings    Profile / Brokerage / Guardrails / System
 *
 * Modals/overlays (FocalCard, not routes — opened via useFocal() set()):
 *   chart       — bars overlay for a symbol
 *   order       — order ticket overlay for a symbol
 *   backtest    — backtest workbench overlay
 *   strategies  — strategy comparison overlay
 *
 * v0.5.4 AE-072: the "/chart/:symbol" + "/order/:symbol" routes documented
 * here in earlier revisions never existed. The catch-all route below
 * redirected them to /today, hiding the typo. FocalCard owns these as
 * overlays; deeplinks use ?symbol= + ?view= on the primary tabs.
 */
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { FocalProvider } from './lib/focal';
import { ToastProvider } from './lib/toast';
import { ConfirmProvider } from './lib/confirm';
import { Login } from './components/Login';
import { MorningBriefPane } from './components/MorningBriefPane';
import { PortfolioPage } from './components/pages/PortfolioPage';
import { ResearchPage } from './components/pages/ResearchPage';
import { ApprovalsPage } from './components/pages/ApprovalsPage';
import { SettingsPanel } from './components/SettingsPanel';
import { ChatDock } from './components/ChatDock';
import { CommandPalette } from './components/CommandPalette';
import { FillToasts } from './components/FillToasts';
import { FocalCard } from './components/FocalCard';
import { TickerTape } from './components/TickerTape';
import { usePending } from './lib/usePending';
import { useState, createContext, useContext, useCallback, useMemo } from 'react';
import { ModeTogglePopover } from './components/ModeTogglePopover';

// ---------------------------------------------------------------------------
// Chat open/close context (for Debate button → chat integration)
// ---------------------------------------------------------------------------
interface ChatCtx {
  open: boolean;
  setOpen: (v: boolean) => void;
  openWithMessage: (msg: string) => void;
  pendingMessage: string | null;
  clearPendingMessage: () => void;
}

const ChatContext = createContext<ChatCtx | null>(null);

// No-op stub used when a component that uses useChatPanel is rendered
// outside an AppShell (e.g. unit tests, isolated FocalCard preview).
// Strict-throw was the old behaviour but it made every page test require
// a ChatContext wrapper which added noise without catching real bugs.
const CHAT_PANEL_STUB: ChatCtx = {
  open: false,
  setOpen: () => {},
  openWithMessage: () => {},
  pendingMessage: null,
  clearPendingMessage: () => {},
};

export function useChatPanel(): ChatCtx {
  return useContext(ChatContext) ?? CHAT_PANEL_STUB;
}

// ---------------------------------------------------------------------------
// Inner shell (authenticated)
// ---------------------------------------------------------------------------
function AppShell() {
  const { me, signOut } = useAuth();
  const { count: pendingCount } = usePending();
  useNavigate(); // retained for router context; unused directly
  const [chatOpen, setChatOpen] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [modePopover, setModePopover] = useState(false);
  const isLive = me?.mode === 'live';

  const openWithMessage = useCallback((msg: string) => {
    setPendingMessage(msg);
    setChatOpen(true);
  }, []);

  const clearPendingMessage = useCallback(() => setPendingMessage(null), []);

  const chatCtx = useMemo<ChatCtx>(() => ({
    open: chatOpen,
    setOpen: setChatOpen,
    openWithMessage,
    pendingMessage,
    clearPendingMessage,
  }), [chatOpen, openWithMessage, pendingMessage, clearPendingMessage]);

  if (!me) return null;

  return (
    <ChatContext.Provider value={chatCtx}>
      <div className={`min-h-screen flex flex-col ${isLive ? 'live-tint' : ''}`}>
        {/* Top bar — border color signals mode */}
        <header className={`flex items-center justify-between border-b bg-bg-elev/60 backdrop-blur px-3 md:px-4 py-2 gap-2 border-t-2 ${isLive ? 'border-t-bad border-b-edge' : 'border-t-ok border-b-edge'}`}>
          <div className="flex items-center gap-2">
            <NavLink to="/today" className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-bg-card border border-edge flex items-center justify-center text-accent text-xs font-bold">m</div>
              <span className="font-semibold text-sm hidden sm:inline">Meeri</span>
            </NavLink>
            <div className="relative">
              <button
                onClick={() => setModePopover((v) => !v)}
                className={`${isLive ? 'pill-live' : 'pill-sim'} cursor-pointer hover:opacity-80 transition`}
                data-testid="mode-pill"
                title="Click to switch trading mode"
              >
                {me.mode.toUpperCase()}
              </button>
              {modePopover && <ModeTogglePopover onClose={() => setModePopover(false)} />}
            </div>
            {isLive && <span className="pill text-warn border-[#5c4720]">REAL MONEY</span>}
          </div>

          {/* Ticker tape (hidden on mobile) */}
          <div className="flex-1 mx-3 overflow-hidden hidden md:block">
            <TickerTape />
          </div>

          <div className="flex items-center gap-1.5 text-xs text-ink-mute">
            <span className="hidden sm:inline">{me.user.email}</span>
            <button onClick={() => setChatOpen((v) => !v)} className={`btn text-xs ${chatOpen ? 'border-accent text-accent' : ''}`} title="toggle chat">
              <ChatIcon />
            </button>
            <button onClick={signOut} className="btn text-xs">sign out</button>
          </div>
        </header>

        {/* Tab navigation */}
        <nav className="flex items-center gap-1 border-b border-edge bg-bg px-3 md:px-4 py-1 overflow-x-auto">
          <TabLink to="/today">Today</TabLink>
          <TabLink to="/portfolio">Portfolio</TabLink>
          <TabLink to="/research">Research</TabLink>
          <TabLink to="/approvals" badge={pendingCount > 0 ? pendingCount : undefined}>Approvals</TabLink>
          <TabLink to="/settings">Settings</TabLink>
        </nav>

        <CommandPalette />
        <FocalCard />
        {/* ORDER_FILLED SSE toast bridge (v0.5.6 AE-036) — renders nothing */}
        <FillToasts />

        {/* Main content + optional chat */}
        <div className="flex-1 flex overflow-hidden">
          <main className="flex-1 overflow-y-auto p-3 md:p-4">
            <Routes>
              <Route path="/today" element={<MorningBriefPane />} />
              <Route path="/portfolio" element={<PortfolioPage />} />
              <Route path="/research" element={<ResearchPage />} />
              <Route path="/approvals" element={<ApprovalsPage />} />
              <Route path="/settings" element={<SettingsPanel />} />
              <Route path="*" element={<Navigate to="/today" replace />} />
            </Routes>
          </main>

          {/* Collapsible chat panel (R3) */}
          {chatOpen && (
            <aside className="w-full md:w-[420px] md:max-w-[40vw] border-l border-edge flex flex-col
                              fixed md:relative inset-0 md:inset-auto z-40 bg-bg md:bg-transparent">
              <div className="md:hidden flex items-center justify-between border-b border-edge px-3 py-2">
                <span className="text-xs text-ink-mute">Chat</span>
                <button className="btn text-xs" onClick={() => setChatOpen(false)}>close</button>
              </div>
              <ChatDock />
            </aside>
          )}
        </div>

        {/* Floating chat button (mobile, when chat is closed) */}
        {!chatOpen && (
          <button
            onClick={() => setChatOpen(true)}
            className="md:hidden fixed bottom-4 right-4 z-30 w-12 h-12 rounded-full bg-accent text-bg flex items-center justify-center shadow-lg"
            aria-label="Open chat"
          >
            <ChatIcon />
          </button>
        )}
      </div>
    </ChatContext.Provider>
  );
}

function ChatIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function TabLink({ to, children, badge }: { to: string; children: React.ReactNode; badge?: number }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `text-xs px-3 py-1.5 rounded-md transition whitespace-nowrap flex items-center gap-1.5 ${
          isActive
            ? 'bg-bg-elev border border-edge text-ink font-medium'
            : 'text-ink-mute hover:text-ink'
        }`
      }
    >
      {children}
      {badge !== undefined && badge > 0 && (
        <span className="px-1.5 rounded bg-warn text-bg num text-[10px] font-bold">{badge}</span>
      )}
    </NavLink>
  );
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------
function Inner() {
  const { me, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-ink-mute text-sm">loading...</div>
    );
  }
  if (!me) return <Login />;
  return (
    <FocalProvider>
      <AppShell />
    </FocalProvider>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <ConfirmProvider>
            <Inner />
          </ConfirmProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
